import { Directive, ElementRef, OnDestroy, OnInit, inject, input } from '@angular/core';
import { ElementInViewportDirective } from '@directives/element-in-viewport/element-in-viewport.directive';
import { ScrollDepthService } from '@services/scroll-depth-service/scroll-depth.service';

const SEEN_RATIO = 0.5;
// Granular steps so sections taller than the viewport still produce enough
// callbacks for the half-viewport-covered rule to be evaluated.
const OBSERVER_THRESHOLDS = [0, 0.125, 0.25, 0.375, 0.5];
// Empty wrappers (e.g. a slideless swiper-wrapper) collapse to ~0 height and
// would instantly reach ratio 1.0 — never count them.
const MIN_SECTION_HEIGHT_PX = 10;
// A section must keep qualifying for this long before it counts — filters out
// elements that only pass through the viewport during initial-load layout shifts.
const SEEN_CONFIRM_DELAY_MS = 300;

/**
 * Marks an element as a scroll-depth section. Composes ElementInViewportDirective
 * for observation and reports to the page-scoped ScrollDepthService — the page
 * component must provide ScrollDepthService and call init().
 */
@Directive({
  selector: '[appTrackScrollDepth]',
  hostDirectives: [ElementInViewportDirective],
})
export class TrackScrollDepthDirective implements OnInit, OnDestroy {
  sectionName = input.required<string>({ alias: 'appTrackScrollDepth' });

  private readonly viewport = inject(ElementInViewportDirective);
  private readonly scrollDepth = inject(ScrollDepthService);
  private readonly elementRef = inject(ElementRef);
  private resizeObserver: ResizeObserver | null = null;
  private confirmTimer: ReturnType<typeof setTimeout> | null = null;
  private seen = false;
  private destroyed = false;

  constructor() {
    // Host directive constructors run before any ngOnInit — safe to configure here.
    this.viewport.viewportThreshold = OBSERVER_THRESHOLDS;
    if (this.scrollDepth.bottomInsetPx > 0) {
      this.viewport.viewportMargin = `0px 0px -${this.scrollDepth.bottomInsetPx}px 0px`;
    }
  }

  ngOnInit(): void {
    // OutputEmitterRef.subscribe — Angular auto-cleans the subscription when the
    // host directive is destroyed (not pipeable; no takeUntilDestroyed needed).
    this.viewport.viewportEntryChange.subscribe((entry: IntersectionObserverEntry): void => this.evaluateEntry(entry));
    this.scrollDepth.onReady((): void => {
      if (this.seen || this.destroyed) return;
      this.viewport.restartObserving();
    });
    this.scrollDepth.onBottomInsetChange((insetPx: number): void => {
      if (this.seen || this.destroyed) return;
      this.viewport.viewportMargin = insetPx > 0 ? `0px 0px -${insetPx}px 0px` : '0px';
      this.viewport.restartObserving();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopWatchingForContent();
    this.cancelConfirmTimer();
  }

  private evaluateEntry(entry: IntersectionObserverEntry): void {
    if (this.seen || !this.scrollDepth.isReady()) return;
    if (entry.boundingClientRect.height < MIN_SECTION_HEIGHT_PX) {
      this.cancelConfirmTimer();
      this.watchForContent();
      return;
    }
    const halfSectionVisible = entry.intersectionRatio >= SEEN_RATIO;
    const halfViewportCovered = !!entry.rootBounds
      && entry.intersectionRect.height >= SEEN_RATIO * entry.rootBounds.height;
    if (!halfSectionVisible && !halfViewportCovered) {
      this.cancelConfirmTimer();
      return;
    }
    if (this.confirmTimer !== null) return;
    this.confirmTimer = setTimeout((): void => this.confirmSeen(), SEEN_CONFIRM_DELAY_MS);
  }

  private confirmSeen(): void {
    this.confirmTimer = null;
    this.seen = true;
    this.stopWatchingForContent();
    this.scrollDepth.markSeen(this.sectionName());
    this.viewport.stopObserving();
  }

  private cancelConfirmTimer(): void {
    if (this.confirmTimer === null) return;
    clearTimeout(this.confirmTimer);
    this.confirmTimer = null;
  }

  private watchForContent(): void {
    if (this.resizeObserver || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
      const height = entries[0]?.contentRect?.height ?? 0;
      if (height < MIN_SECTION_HEIGHT_PX) return;
      this.stopWatchingForContent();
      this.viewport.restartObserving();
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  private stopWatchingForContent(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
}
